import * as React from 'react';
import { UseParticipantsOptions } from '@livekit/components-react';
import { TrackLoop, TrackLoopWithHostFocus } from '../TrackLoop';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { useCustomLayout } from '../../hooks/useCustomLayout';

/** @public */
export interface CustomLayoutProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Pick<UseParticipantsOptions, 'updateOnlyOn'> {
  tracks: TrackReferenceOrPlaceholder[];
}

/**
 * The `CustomLayout` component displays the nested participants in a grid where every participant has the same size,
 * besides the room creator (host) in some cases.
 * @remarks
 * To ensure visual stability when tiles are reordered due to track updates,
 * the component uses the `useVisualStableUpdate` hook.
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <CustomLayout tracks={tracks}>
 *     <ParticipantTile />
 *   </CustomLayout>
 * <LiveKitRoom>
 * ```
 * @public
 */
export function CustomLayout({ tracks }: CustomLayoutProps) {
  const gridEl = React.createRef<HTMLDivElement>();
  const { focusHostRow, radius, profile_img_dimensions } = useCustomLayout(gridEl, tracks.length);

  return (
    <>
      {focusHostRow
        ? <TrackLoopWithHostFocus key={String(focusHostRow)} radius={radius} profile_img_dimensions={profile_img_dimensions} tracks={tracks} />
        : <div ref={gridEl} className="custom-layout">
            <TrackLoop tracks={tracks} radius={radius} profile_img_dimensions={profile_img_dimensions} />
          </div>
      }
    </>
  );
}
